import React, { createContext, useContext, useRef, useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { guardarAjusteEnFicha, AJUSTE_RECORRIDO, AJUSTE_RECORRIDO_PANTALLAS } from '../lib/ajustesEnFicha';
import { arrancarRecorrido, pararRecorrido, preguntarElTour, prepararPantalla, pantallaDe, paradasDeContenido, PANTALLAS, PRIMERA_PANTALLA, RUTA_DE } from '../lib/recorridoGuiado';
import { actuandoComoId } from '../lib/cabeceras';
// ¿Tiene ya macros de verdad que repartir? (ficha 05 del parte de Gonzalo, 4-09). El
// porqué, y por qué vive en lib y no aquí, en `lib/macrosQueRepartir.js`.
import { tieneMacrosQueRepartir } from '../lib/macrosQueRepartir';

// ============================================================================
// EL RECORRIDO DE LA PRIMERA VEZ (doc de Jesús del 21-08, apartado 23), ANCLADO A LA
// PANTALLA desde el 22-09 (Francisco: «que vuelva el recorrido guiado pero bien hecho,
// que los cuadros se muestren donde deben»). Antes eran cinco tarjetas centradas que
// tapaban el Inicio; ahora driver.js apaga todo menos el trozo del que habla y el cuadro
// sale pegado a él (lib/recorridoGuiado.js). Aquí solo vive CUÁNDO sale y dónde se apunta.
//
// Las reglas de comportamiento, decididas en el doc y afinadas el 22-09:
// - LA PRIMERA VEZ que el cliente entra al Inicio, después de los tres pasos del alta
//   (registro, plan, cuestionario), SE LE PREGUNTA si quiere el recorrido asistido
//   (Francisco: «antes de arrancar debe haber una ventana que pregunte»). Solo si dice
//   que sí arranca; si dice que no, se apunta como saltado y no se le vuelve a ofrecer.
// - Si acepta, cada pantalla enseña sus paradas la primera vez que entra en ella
//   (Inicio, Nutrición, Mi semana, Rutina). «Saltar» en cualquiera lo para todo.
// - Se puede repetir desde Mi perfil («Ver el recorrido»), que lo rearma entero.
// - Actuando como un cliente sale igual (para probarlo), pero se apunta en la pestaña y
//   no en la ficha del cliente.
// - El estado (visto/saltado) se persiste EN LA FICHA (ajustes_app.recorrido_0110 desde el 01-10),
//   no solo en el navegador: cambiar de móvil no lo resucita. Y el disparo lee
//   SOLO la ficha, nunca localStorage: en un ordenador compartido el «saltado»
//   de uno no puede tapárselo al siguiente.
// ============================================================================

const OnboardingContext = createContext(null);

export const useOnboarding = () => useContext(OnboardingContext) || {
    // Fallback no-op (por si algún componente se usa fuera del provider)
    startTour: () => {}, resumeTour: () => {}, skipTour: () => {}, notify: () => {}, active: false, available: false, completed: true,
};

export const OnboardingProvider = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { profile, isClient, planUnpaid, refreshProfile } = useAuth();
    // Iba detrás del interruptor `t1_inicio_nuevo`, que se quitó el 04-11: sale a todos.
    // QUIÉN USA LA APP, Y NO SOLO EL ROL «CLIENTE» (Francisco, 25-09: «con mi cuenta no puedo
    // ver el onboarding, creo que ninguna cuenta admin puede»). El admin o el entrenador que
    // entra en «Usar app (modo cliente)» con su propia ficha y su plan la usa igual que un
    // cliente, y se quedaba sin recorrido y sin «Ver el recorrido» en Mi perfil porque aquí se
    // miraba `role === 'client'`. En el panel no sale nunca: solo arranca en las pantallas del
    // cliente (`pantallaDe`). Su estado va a SU ficha, como el de cualquiera.
    const usaLaApp = isClient || !!profile?.plan;

    const [activo, setActivo] = useState(false);

    // Lo que dice la ficha, con un espejo en memoria para el hueco entre «guardado» y
    // «perfil refrescado»: sin él, el efecto volvería a preguntar o a abrir en ese instante.
    // `estado`: undefined (nunca preguntado) | 'aceptado' | 'visto' | 'saltado'.
    const estadoRef = useRef(undefined);
    const vistasRef = useRef(new Set());
    const sincronizadoRef = useRef(false);
    // ACTUANDO COMO UN CLIENTE (el equipo probando), el recorrido sale igual -- es la única
    // forma de verlo tal cual lo ve el cliente --, pero su estado vive en la pestaña
    // (sessionStorage, por cliente) y NUNCA en la ficha: lo que el equipo mira no deja
    // huella en nadie, y el «ahora no» del entrenador no le tapa el recorrido al cliente.
    const enPestana = () => (actuandoComoId() ? `recorrido_0110_actuando_${actuandoComoId()}` : null);
    const leerDePestana = () => {
        try { return JSON.parse(sessionStorage.getItem(enPestana()) || 'null'); } catch (e) { return null; }
    };
    useEffect(() => {
        if (!profile || sincronizadoRef.current) return;
        sincronizadoRef.current = true;
        const propio = enPestana() ? (leerDePestana() || {}) : (profile.ajustes_app || {});
        estadoRef.current = propio[AJUSTE_RECORRIDO];
        vistasRef.current = new Set(propio[AJUSTE_RECORRIDO_PANTALLAS] || []);
    }, [profile]);
    // Que no se pregunte dos veces en la misma sesión aunque el guardado falle.
    const preguntadoRef = useRef(false);

    // ACTUANDO COMO UN CLIENTE NO SE APUNTA NADA (22-09): ese guardado va SIN
    // `X-Actuar-Como` (es del que está delante), así que el «visto» del entrenador se
    // colaba en su propia ficha; y con la cabecera se le marcaría al cliente como saltado
    // un recorrido que no ha visto. Lo que el equipo mira desde Mi perfil no deja huella.
    const apuntar = useCallback((estado, pantallas) => {
        if (estado) estadoRef.current = estado;
        if (pantallas) vistasRef.current = new Set(pantallas);
        if (actuandoComoId()) {
            try {
                sessionStorage.setItem(enPestana(), JSON.stringify({
                    [AJUSTE_RECORRIDO]: estadoRef.current, [AJUSTE_RECORRIDO_PANTALLAS]: [...vistasRef.current] }));
            } catch (e) { /* sin storage: dura lo que dure la pantalla */ }
            return;
        }
        if (estado) guardarAjusteEnFicha(AJUSTE_RECORRIDO, estado);
        if (pantallas) guardarAjusteEnFicha(AJUSTE_RECORRIDO_PANTALLAS, pantallas);
        if (refreshProfile) refreshProfile();
    }, [refreshProfile]);

    // Al acabar las paradas de una pantalla: se apunta la pantalla; con las cuatro, el
    // recorrido entero queda visto. «Saltar» en cualquiera lo para todo.
    const cerrar = useCallback((pantallaVista, estado) => {
        setActivo(false);
        if (estado === 'saltado') { apuntar('saltado'); return; }
        const vistas = [...new Set([...vistasRef.current, pantallaVista])];
        const todas = PANTALLAS.every((p) => vistas.includes(p));
        apuntar(todas ? 'visto' : null, vistas);
    }, [apuntar]);

    // Arranca sobre lo que hay en pantalla. Las pantallas se pintan por partes, así que se
    // espera a que estén las paradas (hasta seis segundos); si no llegan, no sale nada y
    // la pantalla se queda pendiente para la próxima vez.
    const intentoRef = useRef(0);
    //
    // Y SE ESPERA A QUE LA PANTALLA ESTÉ ENTERA (Francisco, 23-09: «Ver el recorrido no se
    // muestra en el celular»). En el teléfono la barra de abajo está desde el primer instante
    // y el recorrido arrancaba en cuanto la veía, antes de que el Inicio pintara nada: una
    // sola parada, «Lo demás está en Más», y fin. Ahora arranca cuando hay paradas DEL
    // CONTENIDO y su número lleva dos vueltas sin cambiar; si en cuatro segundos solo están
    // las fijas, arranca con lo que haya.
    // Al cambiar de pantalla con el recorrido abierto, se quita (ver `pararRecorrido`): esa
    // pantalla sigue pendiente, y el auto-arranque de abajo ofrece la nueva si le toca.
    const pantallaDelTourRef = useRef(null);
    useEffect(() => {
        if (!activo) return;
        if (pantallaDe(location.pathname) === pantallaDelTourRef.current) return;
        intentoRef.current += 1;
        pararRecorrido();
        setActivo(false);
    }, [location.pathname, activo]);

    const arrancar = useCallback((pantallaActual) => {
        const intento = ++intentoRef.current;
        const desde = Date.now();
        let preparada = false;
        let anterior = -1;
        let quieto = 0;
        const probar = () => {
            if (intento !== intentoRef.current) return;
            // Una vez, y con un respiro para que lo abierto se pinte antes de contar paradas.
            if (!preparada && prepararPantalla(pantallaActual)) { preparada = true; setTimeout(probar, 600); return; }
            preparada = true;
            const contenido = paradasDeContenido(pantallaActual);
            quieto = contenido > 0 && contenido === anterior ? quieto + 1 : 0;
            anterior = contenido;
            const pasado = Date.now() - desde;
            const lista = (contenido > 0 && quieto >= 2) || pasado >= 4000;
            if (lista && arrancarRecorrido({ pantalla: pantallaActual, alTerminar: (e) => cerrar(pantallaActual, e) })) {
                pantallaDelTourRef.current = pantallaActual;
                setActivo(true);
                return;
            }
            if (pasado < 6000) setTimeout(probar, 300);
        };
        probar();
    }, [cerrar]);

    // LA PREGUNTA, antes de nada: «¿Quieres el recorrido asistido?».
    //
    // EN MI DÍA, Y EL RECORRIDO EN NUTRICIÓN (Enrique, 04-11). La ventana sale al entrar por
    // primera vez en Mi día tras el cuestionario. «Ahora no» le deja en Mi día; «Venga, vamos
    // a ello» le lleva a Nutrición, y allí arranca solo (el auto-arranque de abajo, en cuanto
    // la pantalla tiene sus preferencias y sus paradas).
    const preguntar = useCallback(async () => {
        preguntadoRef.current = true;
        // «Un pequeño tour» en vez de la pregunta (Jesús, Word del 23-09).
        const quiere = await preguntarElTour();
        if (!quiere) { apuntar('saltado'); return; }
        apuntar('aceptado', []);
        navigate(RUTA_DE[PRIMERA_PANTALLA]);
    }, [apuntar, navigate]);

    // Repetir a mano (Mi perfil): TAMBIÉN con «Un pequeño tour» delante (Francisco, 23-09:
    // «al presionar el botón para rehacerlo obvia la primera pantalla de introducción»).
    // Con «Venga, vamos a ello» se rearma entero y arranca por el Inicio; con «Ahora no»
    // se cierra y no se apunta nada: lo que ya había visto sigue visto.
    const repitiendoRef = useRef(false);
    const startTour = useCallback(async () => {
        if (repitiendoRef.current) return;
        repitiendoRef.current = true;
        try {
            const quiere = await preguntarElTour();
            if (!quiere) return;
            apuntar('aceptado', []);
            arrancar(PRIMERA_PANTALLA);
        } finally {
            repitiendoRef.current = false;
        }
    }, [apuntar, arrancar]);

    // Cuándo cambian las preferencias de Nutrición (ver NutritionPage): para volver a mirar.
    const [prefsTick, setPrefsTick] = useState(0);
    useEffect(() => {
        const avisa = () => setPrefsTick((t) => t + 1);
        window.addEventListener('preferencias-cambian', avisa);
        return () => window.removeEventListener('preferencias-cambian', avisa);
    }, []);

    // Auto-arranque: la primera vez, sobre el Inicio, con el alta terminada; y después,
    // en cada pantalla con paradas que aún no haya visto.
    useEffect(() => {
        if (activo) return;
        // Con «Un pequeño tour» delante (desde Mi perfil), el automático espera: si no, al llegar
        // a Nutrición arrancaba por su cuenta y se comía la pregunta.
        if (repitiendoRef.current) return;
        if (!profile || !sincronizadoRef.current) return;
        // ACTUANDO COMO UN CLIENTE, SIEMPRE (Jesús, 01-10: «en algunas cuentas funciona y en
        // otras no»). El equipo entra para verlo como lo verá el cliente: no se le esconde
        // porque a esa ficha le falte el plan, el acceso, el cuestionario o los macros.
        const actuando = !!actuandoComoId();
        if (!actuando && (!usaLaApp || planUnpaid)) return;
        // CON PLAN Y CON ACCESO, O NADA (fichas 01 y 05 del repaso del 4-09). Esto no miraba
        // el plan: al que tenía el cuestionario hecho y ningún plan se le montaba el recorrido
        // ENCIMA de «Bienvenido a 12EN12 · selecciona un plan», y el overlay se comía el clic
        // del botón («vuelve el tutorial y la URL no cambia»). El orden es plan, alta y
        // después el tutorial: se explica cómo repartir los macros a quien ya puede hacerlo.
        if (!actuando && (!profile.plan || !profile.acceso?.activo)) return;
        if (!actuando && !profile.questionnaire_completed) return;        // el alta primero
        // Y CON MACROS QUE REPARTIR (ficha 05 del mismo parte, cerrada el 13-09). «Alta
        // hecha» no es «tiene macros»: con `macros_training` vacío se le montaban cinco
        // tarjetas sobre cómo repartir unos números que no existen, encima de un Inicio que
        // a la vez le pedía «Configura tus macros». El porqué entero, en lib/macrosQueRepartir.
        if (!actuando && !tieneMacrosQueRepartir(profile)) return;
        if (!actuando && profile.acceso?.motivo === 'caducado') return;   // al caducado no se le enseña a montar nada
        const estado = estadoRef.current;
        if (estado === 'visto' || estado === 'saltado') return;   // nunca se ofrece solo otra vez
        if (!estado) {
            // Nunca preguntado: se pregunta en Mi día (04-11), que es donde entra al terminar
            // el cuestionario y en su primer acceso; no en mitad de otra pantalla.
            if (location.pathname !== RUTA_DE.inicio || preguntadoRef.current) return;
            preguntar();
            return;
        }
        const pantallaActual = pantallaDe(location.pathname);
        if (!pantallaActual) return;
        // EN NUTRICIÓN, CON LAS PREFERENCIAS YA PUESTAS (25-09): mientras la pantalla sea
        // «Configura tus preferencias» (o esté mirando si hace falta), no se arranca. Al
        // guardarlas llega `preferencias-cambian` y este efecto vuelve a pasar.
        if (pantallaActual === 'nutricion' && document.body.dataset.preferencias !== 'listas') return;
        if (vistasRef.current.has(pantallaActual)) return;   // esta pantalla ya la vio
        arrancar(pantallaActual);
    }, [activo, usaLaApp, isClient, profile, planUnpaid, location.pathname, arrancar, preguntar, prefsTick]);

    const value = {
        startTour,
        // Compatibilidad con los consumidores del tour viejo (checklist del Inicio,
        // WelcomePage): resume = empezar de cero, notify ya no hace nada (no hay
        // pasos con «gate»), y completed en true deja el «Continuar recorrido» del
        // checklist apagado para siempre, que ya no existe tal cosa.
        resumeTour: startTour,
        skipTour: () => {},
        notify: () => {},
        active: activo,
        // Lo que mira Mi perfil para ofrecer «Ver el recorrido».
        available: !!(profile && (actuandoComoId() || (usaLaApp && profile.questionnaire_completed
            && profile.plan && profile.acceso?.activo && tieneMacrosQueRepartir(profile)))),
        completed: true,
    };

    return (
        <OnboardingContext.Provider value={value}>
            {children}
        </OnboardingContext.Provider>
    );
};
